import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const PlanSelectionConfirmDialog = ({ 
  isOpen, 
  plan, 
  onConfirm, 
  onCancel,
  isEvaluationMode = false 
}) => {
  const [countdown, setCountdown] = useState(10);
  const [isCountingDown, setIsCountingDown] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setCountdown(10);
      setIsCountingDown(false);
    }
  }, [isOpen]);

  useEffect(() => {
    let timer;
    if (isCountingDown && countdown > 0) {
      timer = setTimeout(() => setCountdown(countdown - 1), 1000);
    } else if (isCountingDown && countdown === 0) {
      handleConfirm();
    }
    return () => clearTimeout(timer);
  }, [isCountingDown, countdown]);

  const handleConfirm = () => {
    setIsCountingDown(false);
    if (onConfirm) {
      onConfirm(plan);
    }
  };

  const handleCancel = () => {
    setIsCountingDown(false);
    if (onCancel) {
      onCancel();
    }
  };

  const startCountdown = () => {
    setIsCountingDown(true);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-6 bg-black/50">
      <div className="bg-surface rounded-2xl p-8 max-w-2xl w-full shadow-2xl border border-border">
        {/* Header */}
        <div className="text-center mb-8">
          <div className={`
            w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4
            ${isEvaluationMode ? 'bg-primary/10' : 'bg-success/10'}
          `}>
            <Icon 
              name={isEvaluationMode ? "BarChart3" : "Play"} 
              size={48} 
              className={isEvaluationMode ? 'text-primary' : 'text-success'}
            />
          </div>
          <h2 className="text-3xl font-bold text-foreground mb-2">
            {isEvaluationMode ? 'Access Evaluation Mode?' : 'Start Training Plan?'}
          </h2>
          <p className="text-xl text-muted-foreground">
            {isEvaluationMode 
              ? 'You will be taken to your progress dashboard' :'Please confirm you want to begin this training session'
            }
          </p>
        </div>

        {/* Plan Details */}
        {!isEvaluationMode && plan && (
          <div className="bg-muted/30 rounded-xl p-6 mb-8 border border-border">
            <h3 className="text-2xl font-semibold text-foreground mb-4">
              {plan?.name}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Phase:</span>
                <span className="font-semibold text-foreground">{plan?.currentPhase}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Duration:</span>
                <span className="font-semibold text-foreground">~{plan?.estimatedDuration} min</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Exercises:</span>
                <span className="font-semibold text-foreground">{plan?.totalExercises}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Progress:</span>
                <span className="font-semibold text-primary">{plan?.progressPercentage}%</span>
              </div>
            </div>
            
            {plan?.canResume && (
              <div className="mt-4 p-4 bg-warning/10 rounded-lg border border-warning/20">
                <div className="flex items-center space-x-2">
                  <Icon name="Clock" size={20} className="text-warning" />
                  <span className="text-warning font-semibold">
                    You can resume your previous session
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Countdown Display */}
        {isCountingDown && (
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-2">
              <span className="text-2xl font-bold text-primary">{countdown}</span>
            </div>
            <p className="text-lg text-muted-foreground">
              {isEvaluationMode ? 'Opening evaluation mode in' : 'Starting session in'} {countdown} seconds
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row gap-4">
          <Button
            variant="outline"
            size="xl"
            fullWidth
            iconName="X"
            iconPosition="left"
            className="text-xl py-4 order-2 sm:order-1"
            onClick={handleCancel}
            disabled={isCountingDown}
          >
            Cancel
          </Button>
          
          {!isCountingDown ? (
            <Button
              variant={isEvaluationMode ? "default" : "success"}
              size="xl"
              fullWidth
              iconName={isEvaluationMode ? "BarChart3" : "Play"}
              iconPosition="right"
              className="text-xl py-4 order-1 sm:order-2"
              onClick={startCountdown}
            >
              {isEvaluationMode ? 'View Progress' : 'Start Training'}
            </Button>
          ) : (
            <Button
              variant="destructive"
              size="xl"
              fullWidth
              iconName="Square"
              iconPosition="left"
              className="text-xl py-4 order-1 sm:order-2"
              onClick={handleCancel}
            >
              Stop Countdown
            </Button>
          )}
        </div>

        {/* Safety Note */}
        <div className="mt-6 p-4 bg-muted/20 rounded-lg border border-border">
          <div className="flex items-start space-x-3">
            <Icon name="Shield" size={20} className="text-primary mt-1" />
            <div>
              <p className="text-sm font-medium text-foreground mb-1">Safety Reminder</p>
              <p className="text-sm text-muted-foreground">
                {isEvaluationMode 
                  ? 'Your progress data is private and secure. You can review your fitness journey at any time.' :'Stop immediately if you feel any discomfort. Emergency assistance is always available.'
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PlanSelectionConfirmDialog;